import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { EventRegistrationService } from '../services/APIService';

// Async thunks for event registrations
export const registerForEvent = createAsyncThunk(
  'registrations/registerForEvent',
  async ({ eventId, notes }, { rejectWithValue }) => {
    try {
      const response = await EventRegistrationService.registerForEvent(eventId, notes);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to register for event');
    }
  }
);

export const fetchMyRegistrations = createAsyncThunk(
  'registrations/fetchMyRegistrations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await EventRegistrationService.getMyRegistrations();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch registrations');
    }
  }
);

export const cancelRegistration = createAsyncThunk(
  'registrations/cancelRegistration',
  async (registrationId, { rejectWithValue }) => {
    try {
      await EventRegistrationService.cancelRegistration(registrationId);
      return registrationId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to cancel registration');
    }
  }
);

export const submitFeedback = createAsyncThunk(
  'registrations/submitFeedback',
  async ({ registrationId, feedback, rating }, { rejectWithValue }) => {
    try {
      await EventRegistrationService.submitFeedback(registrationId, feedback, rating);
      return { registrationId, feedback, rating };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to submit feedback');
    }
  }
);

// Admin thunks
export const fetchEventRegistrations = createAsyncThunk(
  'registrations/fetchEventRegistrations',
  async (eventId, { rejectWithValue }) => {
    try {
      const response = await EventRegistrationService.getEventRegistrations(eventId);
      return { eventId, registrations: response.data };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch event registrations');
    }
  }
);

export const fetchAllRegistrations = createAsyncThunk(
  'registrations/fetchAllRegistrations',
  async (events, { rejectWithValue }) => {
    try {
      // If no events provided, return empty array
      if (!events || events.length === 0) {
        return [];
      }
      
      // Create an array to hold all registrations
      let allRegistrations = [];
      
      // Fetch registrations for each event
      for (const event of events) {
        try {
          const response = await EventRegistrationService.getEventRegistrations(event.id);
          
          // For each registration, add event info
          if (response.data && response.data.data && Array.isArray(response.data.data)) {
            const registrationsWithEventInfo = response.data.data.map(reg => ({
              ...reg,
              // Add event info to each registration
              title: event.title,
              location: event.location,
              date: event.date
            }));
            
            allRegistrations = [...allRegistrations, ...registrationsWithEventInfo];
          }
        } catch (err) {
          console.error(`Error fetching registrations for event ${event.id}:`, err);
          // Continue with other events even if one fails
        }
      }
      
      return allRegistrations;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch all registrations');
    }
  }
);

export const updateRegistrationStatus = createAsyncThunk(
  'registrations/updateRegistrationStatus',
  async ({ registrationId, status }, { rejectWithValue }) => {
    try {
      await EventRegistrationService.updateRegistrationStatus(registrationId, status);
      return { registrationId, status };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update registration status');
    }
  }
);

export const checkInAttendee = createAsyncThunk(
  'registrations/checkInAttendee',
  async (registrationId, { rejectWithValue }) => {
    try {
      await EventRegistrationService.checkInAttendee(registrationId);
      return registrationId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to check in attendee');
    }
  }
);

export const deleteRegistration = createAsyncThunk(
  'registrations/deleteRegistration',
  async (registrationId, { rejectWithValue }) => {
    try {
      await EventRegistrationService.deleteRegistration(registrationId);
      return registrationId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete registration');
    }
  }
);

// Initial state
const initialState = {
  myRegistrations: [],
  eventRegistrations: {},
  allRegistrations: [],
  loading: false,
  registerLoading: false,
  error: null,
  success: null,
};

// Registration slice
const registrationSlice = createSlice({
  name: 'registrations',
  initialState,
  reducers: {
    clearRegistrationSuccess: (state) => {
      state.success = null;
    },
    clearRegistrationError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Register for event
      .addCase(registerForEvent.pending, (state) => {
        state.registerLoading = true;
        state.error = null;
      })
      .addCase(registerForEvent.fulfilled, (state, action) => {
        state.registerLoading = false;
        state.myRegistrations.push(action.payload);
        state.success = 'Successfully registered for event';
      })
      .addCase(registerForEvent.rejected, (state, action) => {
        state.registerLoading = false;
        state.error = action.payload || 'Failed to register for event';
      })

      // Fetch my registrations
      .addCase(fetchMyRegistrations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyRegistrations.fulfilled, (state, action) => {
        state.loading = false;
        state.myRegistrations = action.payload;
      })
      .addCase(fetchMyRegistrations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch registrations';
      })

      // Cancel registration
      .addCase(cancelRegistration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelRegistration.fulfilled, (state, action) => {
        state.loading = false;
        state.myRegistrations = state.myRegistrations.filter(
          registration => registration.id !== action.payload
        );
        state.success = 'Registration cancelled successfully';
      })
      .addCase(cancelRegistration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to cancel registration';
      })

      // Submit feedback
      .addCase(submitFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitFeedback.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.myRegistrations.findIndex(
          registration => registration.id === action.payload.registrationId
        );
        if (index !== -1) {
          state.myRegistrations[index].feedback = action.payload.feedback;
          state.myRegistrations[index].rating = action.payload.rating;
        }
        state.success = 'Feedback submitted successfully';
      })
      .addCase(submitFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to submit feedback';
      })

      // Admin: Fetch event registrations
      .addCase(fetchEventRegistrations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEventRegistrations.fulfilled, (state, action) => {
        state.loading = false;
        state.eventRegistrations[action.payload.eventId] = action.payload.registrations;
      })
      .addCase(fetchEventRegistrations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch event registrations';
      })

      // Admin: Update registration status
      .addCase(updateRegistrationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRegistrationStatus.fulfilled, (state, action) => {
        state.loading = false;
        // Update status in the appropriate event registrations list
        Object.keys(state.eventRegistrations).forEach(eventId => {
          const registrations = state.eventRegistrations[eventId];
          const index = registrations.findIndex(
            registration => registration.id === action.payload.registrationId
          );
          if (index !== -1) {
            registrations[index].status = action.payload.status;
          }
        });
        
        // Update status in allRegistrations list
        const allIndex = state.allRegistrations.findIndex(
          registration => registration.id === action.payload.registrationId
        );
        if (allIndex !== -1) {
          state.allRegistrations[allIndex].status = action.payload.status;
        }
        
        state.success = 'Registration status updated successfully';
      })
      .addCase(updateRegistrationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update registration status';
      })

      // Admin: Check in attendee
      .addCase(checkInAttendee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkInAttendee.fulfilled, (state, action) => {
        state.loading = false;
        const now = new Date().toISOString();
        
        // Update check-in status in the appropriate event registrations list
        Object.keys(state.eventRegistrations).forEach(eventId => {
          const registrations = state.eventRegistrations[eventId];
          const index = registrations.findIndex(
            registration => registration.id === action.payload
          );
          if (index !== -1) {
            registrations[index].check_in_time = now;
            registrations[index].status = 'attended';
          }
        });
        
        // Update check-in status in allRegistrations list
        const allIndex = state.allRegistrations.findIndex(
          registration => registration.id === action.payload
        );
        if (allIndex !== -1) {
          state.allRegistrations[allIndex].check_in_time = now;
          state.allRegistrations[allIndex].status = 'attended';
        }
        
        state.success = 'Attendee checked in successfully';
      })
      .addCase(checkInAttendee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to check in attendee';
      })

      // Admin: Delete registration
      .addCase(deleteRegistration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRegistration.fulfilled, (state, action) => {
        state.loading = false;
        // Remove registration from all event registrations lists
        Object.keys(state.eventRegistrations).forEach(eventId => {
          state.eventRegistrations[eventId] = state.eventRegistrations[eventId].filter(
            registration => registration.id !== action.payload
          );
        });
        
        // Remove registration from allRegistrations list
        state.allRegistrations = state.allRegistrations.filter(
          registration => registration.id !== action.payload
        );
        
        state.success = 'Registration deleted successfully';
      })
      .addCase(deleteRegistration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete registration';
      })

      // Admin: Fetch all registrations
      .addCase(fetchAllRegistrations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllRegistrations.fulfilled, (state, action) => {
        state.loading = false;
        state.allRegistrations = action.payload;
      })
      .addCase(fetchAllRegistrations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch all registrations';
      });
  },
});

// Export actions
export const { clearRegistrationSuccess, clearRegistrationError } = registrationSlice.actions;

// Export selectors
export const selectMyRegistrations = (state) => state.registrations.myRegistrations;
export const selectEventRegistrations = (eventId) => (state) => 
  state.registrations.eventRegistrations[eventId] || [];
export const selectAllRegistrations = (state) => state.registrations.allRegistrations;
export const selectRegistrationLoading = (state) => state.registrations.loading;
export const selectRegisterLoading = (state) => state.registrations.registerLoading;
export const selectRegistrationError = (state) => state.registrations.error;
export const selectRegistrationSuccess = (state) => state.registrations.success;

// Export reducer
export default registrationSlice.reducer;
